<template>
  <div
    v-if="isOpen"
    ref="dialog"
    role="dialog"
    aria-modal="true"
    aria-label="Make a donation"
    tabindex="-1"
    @click="closeModal"
    :class="['donation-modal-backdrop fixed z-20 inset-0 lg:bg-black/[0.70] modal-background modal-close modal-no-margin', isClosing ? 'is-closing' : '']"
    style="margin: 0 !important; top: 0 !important; left: 0 !important; right: 0 !important; bottom: 0 !important;"
  >
      <div class="donation-modal-panel modal-close w-full lg:w-[max(774px,48.375rem)] z-40">
        <button
          v-if="shouldShowCloseIcon"
          ref="closeButton"
          type="button"
          aria-label="Close donation dialog"
          @click.stop="beginClose"
          class="donation-modal-close modal-close cursor-pointer z-50 absolute bg-pink rounded-full flex items-center justify-center p-2"
        >
          <svg class="fill-current text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18"><path d="M14.53 4.53l-1.06-1.06L9 7.94 4.53 3.47 3.47 4.53 7.94 9l-4.47 4.47 1.06 1.06L9 10.06l4.47 4.47 1.06-1.06L10.06 9z"></path></svg>
        </button>
        <div class="donation-modal-viewport">
        <div class="donation-modal-card flex flex-col lg:flex-row w-full relative">
        <div class="donation-modal-hero-column w-full lg:w-1/2">
          <div
            :class="[heroHeightClasses, 'donation-modal-hero bg-gradient-to-b from-purple to-pink lg:rounded-tl-3xl lg:rounded-bl-3xl', { 'hero-panel--with-trust': hasHeroTrust }]"
          >
            <!-- Mission copy swaps per step (crossfade via <Transition>).
                 :key on h1 forces Vue to treat each copy change as an
                 enter/leave pair. Step 1 keeps the foundation's mission
                 as the "why"; steps 2–5 carry step-specific narrative.

                 On Step 2 the inner div switches to flex-col so the
                 hero-trust block stacks under the headline. Trust list
                 renders OUTSIDE the <transition> so it stays put while
                 the h1 crossfades between steps (the transition needs
                 a single child to manage). -->
            <div
              class="bg-black bg-opacity-50 h-full flex items-center justify-center lg:rounded-tl-3xl lg:rounded-bl-3xl relative"
              :class="{ 'hero-inner--with-trust': hasHeroTrust }"
            >
              <transition name="impact-copy" mode="out-in">
                <h1
                  :key="impactCopy"
                  class="font-display font-extrabold text-white text-4xl px-4 lg:px-6 lg:text-5xl text-center leading-[1.05]"
                >{{ impactCopy }}</h1>
              </transition>

              <!-- Step 2 only: 3 trust signals living in the hero
                   panel. Icons reuse the SVG paths previously used by
                   PaymentForm's now-deleted in-form trust pillars so
                   the visual language stays consistent — only the
                   background context changes (white card → dark
                   purple hero). Brand colors (blue/pink/green) pop
                   against the purple gradient. -->
              <div v-if="hasHeroTrust" class="hero-trust" role="list" aria-label="Donation guarantees">
                <div class="hero-trust__item" role="listitem">
                  <span class="hero-trust__icon hero-trust__icon--shield" aria-hidden="true">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                      <path d="M9 12l2 2 4-4"/>
                    </svg>
                  </span>
                  <span class="hero-trust__label">256-bit TLS via Stripe</span>
                </div>
                <div class="hero-trust__item" role="listitem">
                  <span class="hero-trust__icon hero-trust__icon--heart" aria-hidden="true">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                    </svg>
                  </span>
                  <span class="hero-trust__label">Funds the research</span>
                </div>
                <div class="hero-trust__item" role="listitem">
                  <span class="hero-trust__icon hero-trust__icon--receipt" aria-hidden="true">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M14 3H7a2 2 0 0 0-2 2v15.5a.5.5 0 0 0 .8.4L8 19l2.2 1.9a.5.5 0 0 0 .6 0L13 19l2.2 1.9a.5.5 0 0 0 .8-.4V9z"/>
                      <path d="M14 3l5 6h-5z"/>
                      <path d="M9 13h6M9 16h4"/>
                    </svg>
                  </span>
                  <span class="hero-trust__label">Tax-deductible 501(c)(3)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="donation-modal-form w-full lg:w-1/2 flex flex-col justify-start items-start relative bg-white lg:rounded-br-3xl lg:rounded-tr-3xl">
          <!-- Branded notifications toaster. Must sit OUTSIDE the step
               fieldsets — those fieldsets carry a Tailwind `transform`
               class for their slide animation, and a transformed
               ancestor becomes the containing block + stacking context
               cap for position:fixed descendants. Mounted here (form
               column, no transform) so the library's position:fixed
               scopes to the panel (nearest transformed ancestor) and
               z-10000 stacks above the stepper (z-5). Result:
               notifications float inside the widget, anchored to the
               panel, above the pager bar. -->
          <widget-notifications />

          <!-- Stepper: 5 numbered circles with noun labels. Replaces the
               thin pink loading-bar-style progress indicator with an
               intentional step nav. Emotional narrative is carried by
               the left column's mission headline. Hides while the
               tribute swing is open so the user focuses on the
               dedication form. -->
          <!-- Progress indicator: row of dots with a green pill marking
               the current step. Matches the compact dot-pattern Rick
               pointed to on Lovable — no continuous fill bar, no labels.
               Passed steps: small green dots. Active step: wider green
               pill. Upcoming: light gray dots. -->
          <div
            class="donation-progress"
            :class="{ 'is-hidden': isTributeOpen }"
            role="progressbar"
            :aria-label="'Step ' + currentStepIndex + ' of ' + steps.length"
            :aria-valuenow="currentStepIndex"
            aria-valuemin="1"
            :aria-valuemax="steps.length"
            :aria-hidden="isTributeOpen ? 'true' : 'false'"
          >
            <div class="donation-progress__dots">
              <span
                v-for="(s, i) in steps"
                :key="s.key"
                class="donation-progress__dot"
                :class="{
                  'is-passed': isStepComplete(i),
                  'is-active': isStepActive(i),
                }"
                aria-hidden="true"
              ></span>
            </div>
          </div>
              
              <transition
                enter-from-class="-translate-x-full"
                enter-to-class="translate-x-0"
                leave-from-class="translate-x-0"
                :leave-to-class="this.donationWidgetState.leaveToDirection"
              >
                <fieldset
                  v-if="donationWidgetState.steps.donation.isInView"
                  ref="donationStep"
                  :class="{ 'donation-modal-step--tribute-open': isTributeOpen }"
                  class="donation-modal-step transition-all duration-[520ms] ease-[cubic-bezier(0.22,1,0.36,1)] pt-14 w-full h-full absolute top-0 z-auto transform"
                >
                  <donation-form></donation-form>
                </fieldset>
              </transition>
              
              <transition
                :enter-from-class="this.donationWidgetState.enterFromDirection"
                enter-to-class="translate-x-0"
                leave-from-class="translate-x-0"
                :leave-to-class="this.donationWidgetState.leaveToDirection"
              >
                <fieldset
                  v-if="donationWidgetState.steps.payment.isInView"
                  class="donation-modal-step transition-all duration-[520ms] ease-[cubic-bezier(0.22,1,0.36,1)] pt-14 w-full h-full absolute top-0 z-auto transform"
                >
                  <payment-form></payment-form>
                </fieldset>
              </transition>

              <transition
                :enter-from-class="this.donationWidgetState.enterFromDirection"
                enter-to-class="translate-x-0"
                leave-from-class="translate-x-0"
                :leave-to-class="this.donationWidgetState.leaveToDirection"
              >
                <fieldset
                  v-if="donationWidgetState.steps.employerMatch && donationWidgetState.steps.employerMatch.isInView"
                  class="donation-modal-step transition-all duration-[520ms] ease-[cubic-bezier(0.22,1,0.36,1)] pt-14 w-full h-full absolute top-0 z-auto transform"
                >
                  <employer-match-form></employer-match-form>
                </fieldset>
              </transition>

              <transition
                :enter-from-class="this.donationWidgetState.enterFromDirection"
                enter-to-class="translate-x-0"
                leave-from-class="translate-x-0"
                :leave-to-class="this.donationWidgetState.leaveToDirection"
              >
                <fieldset
                  v-if="donationWidgetState.steps.contact.isInView"
                  class="donation-modal-step transition-all duration-[520ms] ease-[cubic-bezier(0.22,1,0.36,1)] pt-14 w-full h-full absolute top-0 z-auto transform"
                >
                  <contact-form></contact-form>
                </fieldset>
              </transition>
              
              <transition
                :enter-from-class="this.donationWidgetState.enterFromDirection"
                enter-to-class="translate-x-0"
                leave-from-class="translate-x-0"
                :leave-to-class="this.donationWidgetState.leaveToDirection"
              >
                <fieldset
                  v-if="donationWidgetState.steps.thankYou.isInView"
                  class="donation-modal-step transition-all duration-[520ms] ease-[cubic-bezier(0.22,1,0.36,1)] pt-14 w-full h-full absolute top-0 z-auto transform"
                >
                  <thank-you></thank-you>
                </fieldset>
              </transition>
            
          </div>
        </div>
      </div>
      </div>
    </div>
</template>

<script>
  import donationForm from './DonationForm'
  import contactForm from './ContactForm'
  import paymentForm from './PaymentForm'
  import thankYou from './ThankYou'
  import { watch } from 'vue'
  import { acquirePageScrollLock } from '../../composables/usePageScrollLock'

  export default {
    name: 'make-donation-widget',
    props: {
      campaignId: {
        type: String,
        required: false,
      },

      campaign: {
        required: false,
      },

      pledgeId: {
        required: false,
        default: '',
      },

      pledge: {
        required: false,
        default: '',
      },

      showCloseIcon: {
        type: Boolean,
        required: false,
        default: true,
      },
      shouldOpenModal: {
        type: Boolean,
        required: false,
        default: false,
      },
      /**
       * Optional per-step bold copy override sourced from the foundation
       * campaign's template content (Donate Copy editor section). Five
       * slots indexed by activeStepIndex; any null/empty slot falls
       * through to DEFAULT_IMPACT_COPY below. Lets admins customize one
       * line without having to override all five.
       */
      impactCopyOverride: {
        type: Array,
        required: false,
        default: null,
      },
    },

    components: { donationForm, contactForm, paymentForm, thankYou },

    data() {
      return {
        donationWidgetState: {},
        isOpen: this.shouldOpenModal,
        isClosing: false,
        timeoutId: null,
        closeAnimationTimeoutId: null,
        // Set by the DonationForm via emitter when the tribute swing
        // opens/closes. Hides the stepper so the user's focus is on
        // the dedication panel at that moment.
        isTributeOpen: false,
      }
    },

    computed: {
      shouldShowCloseIcon() {
        return this.showCloseIcon
      },
      // Ordered list used to render the stepper. Labels are noun-only so
      // they fit in ~50px under a 24px circle on mobile without truncation.
      steps() {
        return [
          { key: 'donation',      label: 'Amount' },
          { key: 'payment',       label: 'Card'   },
          { key: 'employerMatch', label: 'Match'  },
          { key: 'contact',       label: 'You'    },
          { key: 'thankYou',      label: 'Done'   },
        ]
      },
      // 0-based index of the currently visible step. Anchors the stepper's
      // filled/active/upcoming states. Falls back to 0 if no step reports
      // isInView (e.g., between transitions).
      activeStepIndex() {
        const s = this.donationWidgetState.steps
        const order = ['donation', 'payment', 'employerMatch', 'contact', 'thankYou']
        for (let i = 0; i < order.length; i++) {
          const k = order[i]
          if (s[k] && s[k].isInView) return i
        }
        return 0
      },
      // 1-based step number for the aria-label on the stepper nav.
      currentStepIndex() {
        return this.activeStepIndex + 1
      },
      // Left-column mission headline that evolves per step. Keyed to
      // activeStepIndex so the <Transition> crossfades on step change.
      // Each line ties to what the user is doing RIGHT NOW and to the
      // foundation's mission (research that ends terminal diagnoses for
      // metastatic breast cancer patients).
      //
      //   1 Amount  — the foundation's mission as the "why"
      //   2 Card    — "every dollar" frames the payment moment
      //   3 Match   — "double" frames the employer-match step
      //   4 You     — "you are why" frames personal-info capture
      //   5 Done    — "today you" frames the completion moment
      // True only on the Payment (Card) step. Drives the trust-pillar
      // relocation into the hero panel: pillars semantically belong to
      // the card-entry moment ("Encrypted via Stripe", "Deductible
      // 501(c)(3) receipt"). Showing them under "Real research. Real
      // patients." on Step 1 would read as a non-sequitur, so other
      // steps render the standard hero (no trust).
      hasHeroTrust() {
        const s = this.donationWidgetState && this.donationWidgetState.steps
        return !!(s && s.payment && s.payment.isInView)
      },

      // Tailwind height classes for the hero panel. Step 2 needs the
      // hero to auto-grow on mobile to fit the headline PLUS the trust
      // list below it. From lg up the hero fills the card, whose size the
      // desktop .donation-modal-panel rule sets for every step.
      heroHeightClasses() {
        return this.hasHeroTrust
          ? 'min-h-[300px] lg:h-full'
          : 'h-[20vh] lg:h-full'
      },

      impactCopy() {
        // Per-step narrative arc:
        //   0 Amount  — open with urgency + Side-Out's "real" mission
        //   1 Card    — anchor the act of giving in concrete impact
        //   2 Match   — brand-distinctive proof point for the multiplier ask
        //   3 You     — personalize the patient connection
        //   4 Done    — celebrate the donor's role in creating tomorrows
        // Defaults are referenced as a constant outside the array literal
        // so the editor's placeholder list (DonationWidgetSectionEditor)
        // can mirror them — keep these lists in sync.
        const defaults = [
          'Real research. Real patients. Right now.',
          'Your gift funds personalized cancer treatment.',
          'Athletes funding science. It works.',
          'No person living with MBC should face this alone.',
          'Tomorrows matter. You make them possible.',
        ]
        const idx = this.activeStepIndex
        const fallback = defaults[idx] || defaults[0]
        // Check the per-campaign override array; per-slot fallback so an
        // admin can customize one line without having to override all five.
        const override = this.impactCopyOverride && this.impactCopyOverride[idx]
        if (typeof override === 'string' && override.trim() !== '') {
          return override
        }
        return fallback
      },
    },

    created() {
      watch(
        () => this.donationWidgetState,
        () => {
          this.resetTimeout();
        },
        { deep: true }
      );
      this.donationWidgetState = this.$store.getters.donationWidget
      if (!this.donationWidgetState.campaignId) {
        this.$store.dispatch('addCampaignId', this.campaignId)
        this.donationWidgetState = this.$store.getters.donationWidget
      }
      this.$store.dispatch('updateCampaign', this.campaign)

        if (this.pledgeId) {
            this.$store.dispatch('addPledgeId', this.pledgeId)
        }
      if (this.pledge) {
        this.$store.dispatch('updatePledge', this.pledge)
      }

      this.emitter.on('open-donation-modal', this.openModal)

      // Esc closes the modal. Attached at the document level so the user
      // doesn't have to tab focus into the modal first.
      this._escHandler = (e) => {
        if (!this.isOpen || this.isClosing) return
        if (e.key === 'Escape') this.beginClose()
        if (e.key === 'Tab') this.trapFocus(e)
      }
      document.addEventListener('keydown', this._escHandler)

      // Hide the stepper while the tribute swing is open so the
      // dedication form owns the full form column.
      this._onTributeOpened = () => {
        if (this.isTributeOpen || !this.isOpen) return
        const step = this.$refs.donationStep
        this._donationScrollBeforeTribute = step?.scrollTop || 0
        if (step) step.scrollTop = 0
        this.isTributeOpen = true
      }
      this._onTributeClosed = () => {
        // DonationForm also broadcasts its initial closed state on mount.
        if (!this.isTributeOpen) return
        const step = this.$refs.donationStep
        const scrollTop = this._donationScrollBeforeTribute || 0
        this._donationScrollBeforeTribute = 0
        this.isTributeOpen = false
        this.$nextTick(() => {
          if (this.isOpen && !this.isTributeOpen && step && step === this.$refs.donationStep) {
            step.scrollTop = scrollTop
          }
        })
      }
      this.emitter.on('tribute-opened', this._onTributeOpened)
      this.emitter.on('tribute-closed', this._onTributeClosed)
    },

    mounted() {
      if (this.isOpen) this.openModal()
      // Dev-only step previews — gated to non-prod builds. Auto-open
      // the modal and jump straight to a given step so the layout
      // renders without walking through prior steps. Form submission
      // won't work in this mode (no real Stripe token from skipped
      // Step 1) but rendering does, which is all these flags exist
      // for. Webpack/Mix dead-codes the guarded blocks in `npm run prod`.
      if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
        const params = new URLSearchParams(window.location.search)
        const previewStep = {
          step2: 'payment',
          step4: 'contact',
        }[
          ['step2', 'step4'].find((k) => params.get(k) === 'preview')
        ]
        if (previewStep) {
          const widget = this.$store.getters.donationWidget
          Object.keys(widget.steps).forEach((key) => {
            widget.steps[key].isInView = (key === previewStep)
          })
          this.$nextTick(() => {
            this.emitter.emit('open-donation-modal')
          })
        }
      }
    },

    beforeUnmount() {
      this.emitter.off('open-donation-modal', this.openModal)
      this.restorePage()
      if (this._escHandler) {
        document.removeEventListener('keydown', this._escHandler)
      }
      this.emitter.off('tribute-opened', this._onTributeOpened)
      this.emitter.off('tribute-closed', this._onTributeClosed)
      clearTimeout(this.timeoutId)
      clearTimeout(this.closeAnimationTimeoutId)
    },

    methods: {
      openModal(payload = {}) {
        if (!this._releasePageScroll) {
          this._previouslyFocused = payload.returnFocus || document.activeElement
          this._releasePageScroll = acquirePageScrollLock()
        }
        clearTimeout(this.closeAnimationTimeoutId)
        this.isClosing = false
        this.isOpen = true
        this.$nextTick(() => (this.$refs.closeButton || this.$refs.dialog)?.focus({ preventScroll: true }))
      },
      trapFocus(event) {
        const dialog = this.$refs.dialog
        if (!dialog) return
        const scope = dialog.querySelector('.tribute-swing.is-open, .monthly-overlay') || dialog
        const controls = [...scope.querySelectorAll('button, input, select, textarea, a[href], [tabindex]')]
          .filter(el => !el.disabled && el.tabIndex >= 0 && el.getClientRects().length
            && !el.closest('[aria-hidden="true"], [inert]'))
        const first = controls[0]
        const last = controls[controls.length - 1]
        if (!first) {
          event.preventDefault()
          dialog.focus({ preventScroll: true })
        } else if (event.shiftKey && (document.activeElement === first || !scope.contains(document.activeElement))) {
          event.preventDefault()
          last.focus()
        } else if (!event.shiftKey && (document.activeElement === last || !scope.contains(document.activeElement))) {
          event.preventDefault()
          first.focus()
        }
      },
      restorePage() {
        if (!this._releasePageScroll) return
        this._releasePageScroll()
        this._releasePageScroll = null
        this.$nextTick(() => this._previouslyFocused?.focus?.({ preventScroll: true }))
      },
      isStepComplete(i) {
        return i < this.activeStepIndex
      },
      isStepActive(i) {
        return i === this.activeStepIndex
      },
      stepClass(i) {
        return {
          'is-complete': this.isStepComplete(i),
          'is-active':   this.isStepActive(i),
          'is-upcoming': i > this.activeStepIndex,
        }
      },

      closeModal(e) {
        clearTimeout(this.timeoutId)
        if (e.target.classList.contains('modal-close')) {
          this.beginClose()
        }
      },
      // Run the exit animation, then unmount after it finishes.
      // The 280ms matches the longest exit animation duration in the CSS block below
      // (backdrop leave = 260ms + 40ms settle; panel leave = 220ms — 280ms covers both).
      beginClose() {
        if (this.isClosing) return
        this.isClosing = true
        clearTimeout(this.closeAnimationTimeoutId)
        this.closeAnimationTimeoutId = setTimeout(() => {
          this.finalizeClose()
        }, 280)
      },
      finalizeClose() {
        this.isOpen = false
        this.isClosing = false
        this.isTributeOpen = false
        this._donationScrollBeforeTribute = 0
        this.emitter.emit('donation-modal-close')
        this.restorePage()

        // Clear user input so a reopen shows a clean form. Replaces a
        // partial step-flag reset that left tribute, amount, contact +
        // card fields, and successfulTransactionData behind.
        this.$store.dispatch('resetDonationWidget')
      },
      resetTimeout() {
        clearTimeout(this.timeoutId)
        if (this.donationWidgetState.steps.thankYou.isInView) {
          this.timeoutId = setTimeout(() => {
            // Trigger the exit animation for the thank-you auto-close too
            this.beginClose()
          }, 10000) // 10 seconds
        }
      },
    },
  }
</script>

<style>
/* The viewport scrolls the stacked mobile card; each animated step owns its
   overflow inside the clipped form column. The page itself stays in place. */
.donation-modal-backdrop {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
}
.donation-modal-panel {
  position: relative;
  height: 100%;
  min-height: 0;
  background: white;
}
.donation-modal-viewport {
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;
}
.donation-modal-card { min-height: 100%; }
.donation-modal-hero-column { flex-shrink: 0; }
.donation-modal-form {
  flex: 1 0 540px;
  height: 540px;
  min-height: 540px;
  overflow: hidden;
}
.donation-modal-step {
  min-width: 0;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding-bottom: max(16px, env(safe-area-inset-bottom));
  -webkit-overflow-scrolling: touch;
}
.donation-modal-step--tribute-open { overflow: hidden; }
.donation-modal-close {
  z-index: 10001;
  top: max(8px, env(safe-area-inset-top));
  right: max(12px, env(safe-area-inset-right));
  width: 44px;
  height: 44px;
}
.donation-modal-close:focus-visible {
  outline: 3px solid white;
  outline-offset: -5px;
}
.donation-modal-step .monthly-overlay__panel {
  max-height: 100%;
  overflow-y: auto;
}
.donation-modal-step .tribute-swing { overflow: hidden; }
.donation-modal-step .tribute-swing__stage {
  overflow-y: auto;
}
.donation-modal-step .tribute-swing__stage > * { flex-shrink: 0; }
.donation-modal-step .tribute-swing__fields {
  flex: 0 0 auto;
  overflow: visible;
}
.donation-modal-step .tribute-swing__stage--choice {
  justify-content: flex-start;
  justify-content: safe center;
}
@media (max-width: 1023px) {
  .donation-modal-hero { height: auto; }
  .donation-modal-hero > div {
    height: auto;
    min-height: 20vh;
    min-height: 20dvh;
    padding-block: 16px;
  }
  .donation-modal-hero h1 { padding-inline: 56px; }
}
@media (min-width: 1024px) {
  .donation-modal-panel .widget-notifications { top: 60px !important; }
  /* The card (width via lg:w-[max(774px,48.375rem)]) grows with the reader's
     browser font size like the rem-based text inside it, but never below the
     774×540 design size that step 1's px-sized pills and close button need:
     774×540 up to 16px, 967.5×675 at "Large" (20px). Capped to the window
     on both axes; when a cap applies, step 1 can still scroll. */
  .donation-modal-panel {
    max-width: calc(100vw - 32px);
    height: min(max(540px, 33.75rem), calc(100vh - 32px));
    height: min(max(540px, 33.75rem), calc(100dvh - 32px));
    border-radius: 24px;
  }
  .donation-modal-viewport { border-radius: inherit; }
  .donation-modal-card { height: 100%; min-height: 0; }
  .donation-modal-hero-column,
  .donation-modal-hero { height: 100%; }
  .donation-modal-form {
    flex: 0 0 50%;
    height: 100%;
    min-height: 0;
  }
}
/* MakeDonationWidget: two-layer enter/leave choreography.
   Backdrop fades + blurs in (320ms cubic-out); panel lifts and scales in
   80ms behind the backdrop (460ms smooth-out). Exit is faster and
   accelerating: panel sinks + fades first (220ms), backdrop clears last.

   Animations are mount-driven (not Vue <Transition>): the entrance runs
   automatically when the element is inserted; the closing flag swaps in
   the exit animation, and the component unmounts after it finishes. */

/* Enter starts with opacity 0 + panel shifted, animations run it back to default */
.donation-modal-backdrop {
  opacity: 0;
  animation: donation-modal-backdrop-in 320ms cubic-bezier(0.4, 0, 0.2, 1) both;
}
.donation-modal-backdrop.is-closing {
  animation: donation-modal-backdrop-out 260ms cubic-bezier(0.4, 0, 1, 1) 40ms both;
}

.donation-modal-backdrop .donation-modal-panel {
  opacity: 0;
  transform: translateY(28px) scale(0.96);
  animation: donation-modal-panel-in 460ms cubic-bezier(0.16, 1, 0.3, 1) 80ms both;
  transform-origin: center;
  will-change: transform, opacity;
}
.donation-modal-backdrop.is-closing .donation-modal-panel {
  animation: donation-modal-panel-out 220ms cubic-bezier(0.4, 0, 1, 1) both;
}

@keyframes donation-modal-backdrop-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}
@keyframes donation-modal-backdrop-out {
  from { opacity: 1; }
  to   { opacity: 0; }
}
@keyframes donation-modal-panel-in {
  from {
    opacity: 0;
    transform: translateY(28px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
@keyframes donation-modal-panel-out {
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(12px) scale(0.985);
  }
}

@media (prefers-reduced-motion: reduce) {
  .donation-modal-backdrop,
  .donation-modal-backdrop.is-closing,
  .donation-modal-backdrop .donation-modal-panel,
  .donation-modal-backdrop.is-closing .donation-modal-panel {
    animation-duration: 80ms !important;
    animation-delay: 0ms !important;
  }
}

/* Form inputs and selects: smooth the border-color swap between
   focus / error states so validation feedback doesn't snap in. */
.donation-modal-panel input,
.donation-modal-panel select,
.donation-modal-panel textarea {
  transition:
    border-color 200ms cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow   200ms cubic-bezier(0.22, 1, 0.36, 1);
}

/* ─── Progress dots ───────────────────────────────────────────
   Compact dot indicator: row of small dots with a wider green pill
   marking the current step. No continuous fill bar, no labels —
   matches the close-up Lovable reference exactly. Passed: small
   green dot. Active: green pill (wider). Upcoming: light gray dot.
   Positioned absolute at top so it overlays the z-0 fieldsets (which
   are also absolute top-0 for the horizontal step slide); fieldsets'
   pt-14 keeps their content clear of the indicator with the tight
   icon-to-dots cadence we settled on for step 1 — applied uniformly
   across every step so each card opens at the same vertical rhythm. */
.donation-progress {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 5;
  padding: 20px 24px 16px;
  background: #ffffff;
  display: flex;
  justify-content: center;
  opacity: 1;
  transform: translateY(0);
  transition:
    opacity 240ms cubic-bezier(0.4, 0, 0.2, 1),
    transform 320ms cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: auto;
}
/* Tribute open: fade + lift the bar out of the way so the user's
   attention is on the dedication form. */
.donation-progress.is-hidden {
  opacity: 0;
  transform: translateY(-8px);
  pointer-events: none;
}

.donation-progress__dots {
  display: flex;
  align-items: center;
  gap: 10px;
}

.donation-progress__dot {
  width: 10px;
  height: 10px;
  border-radius: 9999px;
  background: #e5e7eb; /* gray-200 — upcoming */
  transition:
    background-color 260ms cubic-bezier(0.22, 1, 0.36, 1),
    width            320ms cubic-bezier(0.22, 1, 0.36, 1);
}
.donation-progress__dot.is-passed {
  background: #22c55e; /* green-500 — completed steps */
}
.donation-progress__dot.is-active {
  width: 28px; /* pill: ~2.8x dot diameter */
  background: #22c55e;
}

@media (max-width: 480px) {
  .donation-progress { padding: 14px 14px 10px; }
  .donation-progress__dots { gap: 8px; }
  .donation-progress__dot { width: 8px; height: 8px; }
  .donation-progress__dot.is-active { width: 24px; }
}

/* ─── Impact copy crossfade ──────────────────────────────────────
   Left-column mission headline swaps per step. Uses mode="out-in"
   so the outgoing line finishes its fade before the incoming line
   starts — prevents two overlapping paragraphs colliding in the
   center of the column. Slight y-translate adds a sense of
   forward motion synchronized with the step advance. */
.impact-copy-enter-active,
.impact-copy-leave-active {
  transition:
    opacity   320ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 320ms cubic-bezier(0.22, 1, 0.36, 1);
}
.impact-copy-enter-from {
  opacity: 0;
  transform: translateY(8px);
}
.impact-copy-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@media (prefers-reduced-motion: reduce) {
  .impact-copy-enter-active,
  .impact-copy-leave-active {
    transition-duration: 80ms;
  }
}

/* ─── iOS auto-zoom prevention ───────────────────────────────
   Safari on iOS zooms the viewport whenever the user focuses an
   <input>, <select>, or <textarea> with a computed font-size
   smaller than 16px. The zoomed viewport breaks the widget's
   layout — stepper and form column get pushed off-screen and
   the user can't see the full flow.

   Force every widget input to 16px on small viewports so the
   zoom-on-focus trigger never fires. Desktop density (13–14px)
   is preserved via the viewport width guard. Covers inputs
   inside the widget AND the DTD plugin's injected input, which
   sits under #dd-company-name-input (mounted outside the
   donation widget but on the same page). */
@media (max-width: 640px) {
  .donation-modal-panel input,
  .donation-modal-panel select,
  .donation-modal-panel textarea,
  #dd-company-name-input input,
  #dd-company-name-input select {
    font-size: 16px !important;
  }
  /* EXCEPTION — the custom donation amount is the hero number; keep it big on
     mobile (Rick 2026-06-11: "still too small on mobile"). The blanket 16px
     guard above was clobbering it down to 16px on every phone, which is why
     prior text-size bumps had no effect. id specificity + !important beats the
     guard. Safe re: iOS auto-zoom — that only fires for inputs UNDER 16px, and
     this is well above. */
  .donation-modal-panel #alt_amount_input {
    font-size: 32px !important;
  }
}

/* ─── Standardized back button (.dw-back-pill) ─────────────────
   Single source of truth for the back affordance across every
   step of the donation widget. Gray-outlined pill, left-edge
   chevron, uppercase label, condensed type. Used by PaymentForm
   (Step 2), EmployerMatchForm (Step 3), DonationForm's tribute
   Step 2, and ContactForm (Step 4) so sizing/spacing/colors stay
   synchronized — no per-step drift. Step-specific tweaks (e.g.
   tribute's inline "In Memory Of | change" content) layer on
   top via the __label / __edit child selectors below. */
.dw-back-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.875rem 0.5rem 0.6875rem;
  background: #ffffff;
  border: 1.5px solid #D1D5DB;
  border-radius: 9999px;
  color: #374151;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 0.8125rem;       /* 13px */
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background-color 180ms ease,
    border-color 180ms ease,
    color 180ms ease,
    transform 180ms cubic-bezier(0.22, 1, 0.36, 1);
}
.dw-back-pill:hover {
  background: #F9FAFB;
  border-color: #9CA3AF;
  color: #1F2937;
  transform: translateX(-1px);
}
.dw-back-pill:active { transform: translateX(0); }
.dw-back-pill:focus-visible {
  outline: 2px solid #6B7280;
  outline-offset: 2px;
}
.dw-back-pill__icon {
  width: 0.8125rem;
  height: 0.8125rem;
  flex-shrink: 0;
}
/* --lg modifier: only used on Step 4 (Your Details) where the
   back button sits side-by-side with the primary DONATE button
   and needs to match its visual weight/height for balanced
   composition. Padding + font-size are bumped to mirror the
   .contact__submit dimensions exactly. */
.dw-back-pill--lg {
  padding: 10px 20px 10px 16px;
  font-size: 1rem;            /* 16px to match DONATE */
  gap: 0.5rem;
}
.dw-back-pill--lg .dw-back-pill__icon {
  width: 1rem;
  height: 1rem;
}
/* Tribute Step 2 variant — inline label + "change" hint inside
   the same pill. Visual frame stays standard; the contextual cue
   ("In Memory Of | change") layers on. */
.dw-back-pill__label {
  font-weight: 700;
}
.dw-back-pill__edit {
  font-family: 'Red Hat Display', system-ui, sans-serif;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.16em;
  color: #6B7280;
  opacity: 0.85;
  padding-left: 0.375rem;
  margin-left: 0.125rem;
  border-left: 1px solid #D1D5DB;
  text-transform: uppercase;
}

/* ─── Step 2 hero panel — trust pillars relocated below the headline ─
   On the Payment (Card) step, the editorial trust panel sits inside
   the dark purple hero panel below the mission headline (not the
   right form column).

   Composition: headline (slightly tightened) at top, then three
   confident one-line trust claims with brand-colored icons (blue
   shield / pink heart / green receipt) docked at the bottom of the
   hero column.

   Mobile: the hero auto-grows (heroHeightClasses swaps h-[20vh] for
   min-h-[300px]) so the trust list fits without overflowing. */

/* Outer gradient becomes a flex column so the inner dark overlay
   can flex-grow to match the gradient's height — necessary because
   on mobile the outer uses min-h-[300px] (auto-grows with content),
   and Tailwind's h-full on the inner = height: 100%, which CSS only
   resolves against an explicit parent height (not min-height). With
   only min-h on the outer, h-full silently falls back to "auto" and
   the inner shrinks to its content, leaving the pink gradient
   bleeding through below the dark overlay. Flex-grow sidesteps this
   entire percentage-height quirk. */
.hero-panel--with-trust {
  display: flex;
  flex-direction: column;
}

/* Inner overlay switches from row-centered to column when v=2 is
   active. justify-content stays centered so the whole headline+trust
   block is vertically balanced inside the panel. flex: 1 1 auto makes
   the overlay fill the gradient panel's full height (see above). */
.hero-inner--with-trust {
  flex: 1 1 auto;
  flex-direction: column;
  justify-content: center;
  gap: 1.5rem;
  padding: 1.75rem 1rem;
  min-height: 0;
}
@media (min-width: 1024px) {
  .hero-inner--with-trust {
    gap: 1.75rem;
    padding: 2.25rem 1.5rem;
  }
}

/* Headline weight & layout when paired with the trust list. The h1
   becomes a flex item that grows to fill the space above the trust
   list, and centers its own text inside that grown box. Net effect:
   the headline reads as vertically centered between the panel top and
   the pillar trio while the pillars stay docked at the bottom of the
   hero column. Descendant selector beats Tailwind's single-class
   utility specificity on text-4xl/text-5xl/leading-[1.05] — no
   !important needed. */
.hero-panel--with-trust h1 {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  font-size: 2rem;         /* ~32px, was text-4xl = 36px */
  line-height: 1.05;
  letter-spacing: -0.01em;
}
@media (min-width: 1024px) {
  .hero-panel--with-trust h1 {
    font-size: 2.75rem;    /* 44px, was text-5xl = 48px */
  }
}

/* Trust list — three icon+label rows beneath the headline. Container
   shrinks to the widest pillar (width: max-content) so it can be
   true-centered in the hero panel as a block. With align-items at the
   flex default (stretch), every item fills the container width — so
   icons land in a clean vertical column on the left edge while the
   whole block stays centered. Vertical position is now driven by the
   headline above (flex: 1 1 auto on the h1) which absorbs the panel's
   free space and pushes the pillars down. margin-bottom: 1.5rem gives
   a touch of cushion above the panel's rounded corner. */
.hero-trust {
  display: flex;
  flex-direction: column;
  width: max-content;
  max-width: 100%;
  gap: 0.5rem;
  padding: 0;
  margin: 0 auto 1.5rem;
}

.hero-trust__item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: rgba(255, 255, 255, 0.94);
  font-family: 'Red Hat Display', sans-serif;
  /* 16px everywhere. Was 0.9375rem (15px) on mobile / 1rem on desktop;
     bumped mobile up to match desktop for legibility of the trust pillars
     on the dark hero (client feedback 2026-05-29). */
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.25;
  letter-spacing: 0.005em;
  opacity: 0;
  transform: translateY(6px);
  animation: hero-trust-in 520ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
}
.hero-trust__item:nth-child(2) { animation-delay: 80ms; }
.hero-trust__item:nth-child(3) { animation-delay: 160ms; }
.hero-trust__item:nth-child(4) { animation-delay: 240ms; }

@keyframes hero-trust-in {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .hero-trust__item {
    animation-duration: 1ms;
    animation-delay: 0ms;
  }
}

/* Icon chips — small (24px) circular badges with a glassy white-at-
   12% backdrop so the brand-colored stroke icons read cleanly against
   the dark purple panel background. */
.hero-trust__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  flex-shrink: 0;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(2px);
}
.hero-trust__icon svg {
  width: 0.9375rem;
  height: 0.9375rem;
}
/* Brand-keyed icon stroke colors: blue #56C8F1 (shield), pink #FF7BBC
   (heart — lifted from the default brand pink for higher legibility
   on the dark gradient), green #B9E445 (receipt — likewise lifted
   from the brand green for contrast against purple). */
.hero-trust__icon--shield  { color: #56C8F1; }
.hero-trust__icon--heart   { color: #FF7BBC; }
.hero-trust__icon--receipt { color: #B9E445; }

.hero-trust__label {
  white-space: normal;
}
</style>
